import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { memo, useEffect, useRef, useState } from "react";
import { match } from "ts-pattern";
import { ColorsProvider } from "../providers/ColorsProvider";
import { useDefaultProps } from "../providers/utils/defaultProps";
import { alpha, mergeSx, slugify } from "../system";
import { Box } from "./Box";
import { Stack } from "./Stack";
import { tabClasses } from "./Tab";
export const tabGroupClasses = {
    root: "MinervaTabGroup-root",
    "tab-container": "MinervaTabGroup-tabContainer",
};
export const tabGroupIndicatorClasses = {
    root: "MinervaTabGroupIndicator-root",
    default: "MinervaTabGroupIndicator-default",
    filled: "MinervaTabGroupIndicator-filled",
    outline: "MinervaTabGroupIndicator-outline",
    tonal: "MinervaTabGroupIndicator-tonal",
};
export const tabGroupVariants = ["default", "filled", "outline", "tonal"];
export const TabGroup = memo((props) => {
    const { value: _value, variant = "default", disableIndicatorAnimation = false, density = "comfortable", color = "surface", colorSet, invertColors, className, children, sx, ...rest } = useDefaultProps({ component: "TabGroup", props });
    const ref = useRef(null);
    const [indicatorStyle, setIndicatorStyle] = useState({
        left: "0px",
        width: "0px",
    });
    const value = typeof _value === "string" ? slugify(_value) : _value;
    useEffect(() => {
        if (ref.current) {
            const tabGroup = ref.current.querySelectorAll(`.${tabClasses.selected}-${typeof _value === "string" ? slugify(_value) : _value}`);
            if (tabGroup.length > 0 && tabGroup[0]) {
                const rect = tabGroup[0].getBoundingClientRect();
                const parentRect = ref.current.getBoundingClientRect();
                const relativeLeft = rect.left - parentRect.left;
                setIndicatorStyle({
                    left: `${relativeLeft}px`,
                    width: `${rect.width}px`,
                });
            }
        }
    }, [_value]);
    const paddingX = density === "comfortable" ? 4 : density === "dense" ? 3 : 0;
    const paddingY = match([variant, density])
        .with(["default", "comfortable"], () => 4)
        .with(["default", "dense"], () => 3)
        .with(["filled", "comfortable"], () => 2)
        .with(["filled", "dense"], () => 1)
        .with(["outline", "comfortable"], () => 2)
        .with(["outline", "dense"], () => 1)
        .with(["tonal", "comfortable"], () => 2)
        .with(["tonal", "dense"], () => 1)
        .otherwise(() => 0);
    return (_jsx(ColorsProvider, { color: color, colorSet: colorSet ?? variant === "tonal" ? "container" : "main", invertColors: invertColors ?? (variant !== "filled" && variant !== "tonal"), children: ({ color, background }) => (_jsxs(Box, { ref: ref, className: clsx([tabGroupClasses.root, className]), sx: mergeSx({
                //TabGroup
                position: "relative",
                display: "flex",
                flexDirection: "column",
                //Tab Children
                [`& .${tabClasses.root}`]: {
                    paddingX,
                    paddingY,
                },
                [`& .${tabClasses.default}`]: variant === "default" && {
                    color: alpha(color, 0.32),
                    borderRadius: (theme) => theme.radius.default,
                    [`&.${tabClasses.selected}-${value}`]: {
                        color,
                    },
                },
                [`& .${tabClasses.filled}`]: variant === "filled" && {
                    borderRadius: (theme) => theme.radius.full,
                    color: alpha(background, 0.32),
                    [`&.${tabClasses.selected}-${value}`]: {
                        color,
                        background,
                    },
                },
                [`& .${tabClasses.outline}`]: variant === "outline" && {
                    borderRadius: (theme) => theme.radius.full,
                    color: alpha(color, 0.32),
                    boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.disabled.main}`,
                    [`&.${tabClasses.selected}-${value}`]: {
                        color,
                    },
                },
                [`& .${tabClasses.tonal}`]: variant === "tonal" && {
                    borderRadius: (theme) => theme.radius.full,
                    color: alpha(color, 0.32),
                    [`&.${tabClasses.selected}-${value}`]: {
                        color,
                        background,
                    },
                },
                [`& .${tabClasses.disabled}`]: {
                    [`&.${tabClasses.default}`]: variant === "default" && {
                        color: (theme) => theme.palette.disabled.main,
                    },
                    [`&.${tabClasses.filled}`]: variant === "filled" && {
                        color: (theme) => theme.palette.disabled.onMain,
                        backgroundColor: (theme) => theme.palette.disabled.main,
                    },
                    [`&.${tabClasses.outline}`]: variant === "outline" && {
                        boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.disabled.main}`,
                    },
                    [`&.${tabClasses.tonal}`]: variant === "tonal" && {
                        color: (theme) => theme.palette.disabled.onContainer,
                        backgroundColor: (theme) => theme.palette.disabled.container,
                    },
                },
            }, sx), ...rest, children: [_jsx(Stack, { flexDirection: "row", alignItems: "center", gap: variant === "default" ? 0 : 2, className: tabGroupClasses["tab-container"], sx: {
                        zIndex: 1,
                    }, children: children }), _jsx(Box, { className: clsx([
                        tabGroupIndicatorClasses.root,
                        variant === "default" && tabGroupIndicatorClasses.default,
                        variant === "filled" && tabGroupIndicatorClasses.filled,
                        variant === "outline" && tabGroupIndicatorClasses.outline,
                    ]), sx: {
                        [`&.${tabGroupIndicatorClasses.root}`]: {
                            position: "absolute",
                            bottom: 0,
                            height: 0.5,
                            left: indicatorStyle.left,
                            width: indicatorStyle.width,
                            transition: disableIndicatorAnimation
                                ? "none"
                                : "left 0.3s, width 0.3s",
                        },
                        [`&.${tabGroupIndicatorClasses.default}`]: {
                            background: color,
                            zIndex: 2,
                            borderRadius: (theme) => theme.radius.default,
                        },
                        [`&.${tabGroupIndicatorClasses.filled}`]: {
                            background: background,
                            height: "100%",
                            zIndex: 0,
                            borderRadius: (theme) => theme.radius.full,
                        },
                        [`&.${tabGroupIndicatorClasses.outline}`]: {
                            background: "transparent",
                            height: "100%",
                            zIndex: 2,
                            boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.outline}`,
                            borderRadius: (theme) => theme.radius.full,
                        },
                        [`&.${tabGroupIndicatorClasses.tonal}`]: {
                            borderRadius: (theme) => theme.radius.full,
                        },
                    } })] })) }));
});
//# sourceMappingURL=TabGroup.js.map